import React from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";

import { Breadcrumb, Breadcrumbs } from "@performyard/py-components";

import { useIsNewNavigation } from "../../../api/hooks/defs/navigation";
import { useEmployeeDashboardOrigin } from "../../../hooks/useEmployeeDashboardOrigin";

import { rootCrumbOrDashboard } from "../../../utils/breadcrumbOrigin";
import { employeeDashboardRootCrumb } from "../../../utils/employeeDashboardBreadcrumbs";
import { REVIEW_CYCLES_LIST_HREF, parseReviewsOrigin } from "../../../utils/reviewsBreadcrumbs";

const ViewReviewBreadcrumbs = () => {
	const location = useLocation();
	const isNewNav = useIsNewNavigation();
	const dashboardOrigin = useEmployeeDashboardOrigin({ deep: true });
	const mount = typeof document !== "undefined" ? document.getElementById("view-review-breadcrumbs") : null;

	if (!isNewNav || !mount) return null;
	// A dashboard origin exists but its employee name is still resolving: render nothing rather
	// than flashing the generic "Dashboard" root and relabeling.
	if (dashboardOrigin.status === "loading") return null;

	const reviewName = mount.getAttribute("data-review-name") || "Review";
	const cycleName = mount.getAttribute("data-cycle-name") || "Review Cycle";
	const defaultFrom = mount.getAttribute("data-default-from") || "/";

	const { from, label, kind } = parseReviewsOrigin(location.search);

	let rootItems: Breadcrumb[];
	if (dashboardOrigin.status === "resolved") {
		// Dashboard origins survive new-tab opens, so they outrank the fresh-tab Dashboard anchor.
		rootItems = [employeeDashboardRootCrumb(dashboardOrigin.from, dashboardOrigin.employeeName)];
		if (dashboardOrigin.viaHref) {
			rootItems.push({
				pageId: "breadcrumb-reviews-parent",
				name: `${dashboardOrigin.employeeName}'s Reviews`,
				href: dashboardOrigin.viaHref,
			});
		}
	} else if (kind === "cycle") {
		rootItems = [
			{ pageId: "breadcrumb-review-cycles", name: "Review Cycles", href: REVIEW_CYCLES_LIST_HREF },
			{ pageId: "breadcrumb-review-cycle", name: cycleName, href: from },
		];
	} else if (label) {
		rootItems = [{ pageId: "breadcrumb-reviews-parent", name: label, href: from }];
	} else {
		// `kind`/`label` are fresh-tab-gated upstream, so a fresh tab always lands here.
		rootItems = [
			rootCrumbOrDashboard({ pageId: "breadcrumb-reviews-parent", name: "Reviews of Me", href: defaultFrom }),
		];
	}

	return createPortal(
		<div style={{ padding: "16px 24px 0" }}>
			<Breadcrumbs
				items={[
					...rootItems,
					{ name: reviewName, href: location.pathname, pageId: "breadcrumb-review-current" },
				]}
			/>
		</div>,
		mount
	);
};

export default ViewReviewBreadcrumbs;
